"use client";
import { beginSignup } from '@/lib/plausible/browser';

import { Link } from '@/i18n/navigation';
import { useState, useCallback } from "react";
import { createClient } from '@/utils/supabase/client';
import toast from "react-hot-toast";
import config from "@/config";
import { theme } from "@/styles/theme";
import { useAuthLocale } from "@/hooks/useAuthLocale";

export default function SignInForm({ onSuccess, showBackButton = true, isModal = false, redirectTo = '/pricing' }) {
  const supabase = createClient();
  const [isLoading, setIsLoading] = useState(false);
  const { buildOAuthRedirectUrl, getCurrentLocale } = useAuthLocale();

  // Google Analytics 转换追踪函数
  const gtag_report_conversion = useCallback((url) => {
    const callback = function () {
      if (typeof(url) != 'undefined') {
        window.location = url;
      }
    };
    
    if (typeof gtag !== 'undefined') {
      gtag('event', 'conversion', {
        'send_to': 'AW-16635151357/aTzVCLz9uvwaEP2Hofw9',
        'value': 1.0,
        'currency': 'HKD',
        'event_callback': callback
      });
    }
    
    return false;
  }, []);

  const handleSignup = useCallback(async (e, options) => {
    e?.preventDefault();
    setIsLoading(true);

    try {
      const { type, provider } = options;
      
      // 上报注册转换事件
      gtag_report_conversion();
      
      // 存储回调信息到localStorage
      if (isModal) {
        localStorage.setItem('auth_redirect', redirectTo);
        localStorage.setItem('auth_callback', JSON.stringify({ 
          isModal: true,
          timestamp: Date.now() 
        }));
      }

      // 使用专门的认证语言处理Hook构建重定向URL
      const baseUrl = window.location.origin;
      const redirectURL = isModal 
        ? buildOAuthRedirectUrl(baseUrl, '/api/auth/callback', redirectTo)
        : `${baseUrl}/api/auth/callback`;

      console.log('[SignInForm] Current locale:', getCurrentLocale(), 'Redirect URL:', redirectURL);

      beginSignup();
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: redirectURL,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        },
      });

      if (error) throw error;
      
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Sign in failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [supabase, isModal, redirectTo, gtag_report_conversion, buildOAuthRedirectUrl, getCurrentLocale]);

  return (
    <div className={`
      ${isModal ? 'w-full' : 'w-full max-w-md'}
      ${isModal ? 'bg-gray-900' : 'bg-gray-900'}
      rounded-lg
      ${isModal ? 'border border-gray-700' : 'border border-gray-700'}
      overflow-hidden
      ${isModal ? '' : 'shadow-2xl shadow-black/30'}
    `}>
      <div className="p-8">
        {/* 标题 */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-white mb-2">
            Sign in to {config.appName}
          </h1>
          <p className="text-gray-300">
            Use your Google account to continue
          </p>
        </div>

        <button
          onClick={(e) => handleSignup(e, { type: "oauth", provider: "google" })}
          disabled={isLoading}
          className="
            w-full
            bg-gradient-to-r from-green-500 via-green-600 to-green-700
            hover:from-green-600 hover:via-green-700 hover:to-green-800
            text-white font-semibold
            rounded-lg px-4 py-4
            flex items-center justify-center gap-3
            transition-all duration-300
            disabled:opacity-50
            disabled:cursor-not-allowed
            mb-6
            shadow-lg shadow-green-500/25
            border border-green-400/20
            hover:shadow-xl hover:shadow-green-500/40
            hover:scale-[1.02]
            active:scale-[0.98]
          "
        >
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <svg className="w-5 h-5" viewBox="0 0 48 48">
              <path
                fill="#FFC107"
                d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.650-.389-3.917z"
              />
              <path
                fill="#FF3D00"
                d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
              />
              <path
                fill="#4CAF50"
                d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
              />
              <path
                fill="#1976D2"
                d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.650-.389-3.917z"
              />
            </svg>
          )}
          <span className="text-lg">Sign in with Google & Get Free Credits</span>
        </button>
        
        {/* 免费积分提示 */}
        <div className="p-4 bg-gray-800 rounded-lg border border-gray-600 mb-6">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
            <div>
              <h3 className="font-medium text-white mb-1">Get FREE Credits Now!</h3>
              <p className="text-sm text-gray-300">
                Sign up today and receive free credits to start creating amazing AI content immediately!
              </p>
            </div>
          </div>
        </div>

        {/* 条款链接 */}
        <div className="text-center text-sm text-gray-400">
          By signing in, you agree to our{" "}
          <Link href="/terms" className="text-green-400 hover:text-green-300 hover:underline transition-colors">
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link href="/privacy" className="text-green-400 hover:text-green-300 hover:underline transition-colors">
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
}
